import { Card, Input, Radio, Select, Tag } from 'antd'
import { CommentIndicator } from 'features/libra/comments/CommentIndicator'
import { useZonePropertyAnchor } from 'features/libra/stepComponents/zoneAnalysis/useZonePropertyAnchor'
import {
  getBaseCoverage,
  InspectionStrategy,
  NiiStrategy,
} from 'features/libra/stepComponents/zoneAnalysis/zoneAnalysisApi'
import { CheckCircle, HelpCircle, RotateCcw } from 'lucide-react'
import { FC, useMemo } from 'react'
import styled, { useTheme } from 'styled-components'

const { TextArea } = Input

const Panel = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`

const HelpText = styled.p`
  font-size: 13px;
  color: ${({ theme }) => theme.forecast.secondaryText};
  margin: 0 0 12px 0;
`

const FieldGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`

const Label = styled.label`
  font-size: 12px;
  font-weight: 600;
  color: ${({ theme }) => theme.forecast.secondaryText};
  text-transform: uppercase;
  letter-spacing: 0.5px;
`

const DecisionTreeCard = styled(Card)`
  margin-bottom: 16px;

  .ant-card-body {
    padding: 16px;
  }
`

const QuestionRow = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 12px;
  margin-bottom: 16px;
  padding: 12px;
  background: ${({ theme }) => theme.forecast.background2};
  border-radius: 6px;
`

const QuestionIcon = styled.div<{ $answered?: boolean }>`
  color: ${({ $answered, theme }) =>
    $answered ? '#4caf50' : theme.forecast.secondaryText};
  flex-shrink: 0;
  margin-top: 2px;
`

const QuestionContent = styled.div`
  flex: 1;
`

const QuestionText = styled.div`
  font-weight: 500;
  margin-bottom: 8px;
  color: ${({ theme }) => theme.forecast.defaultText};
`

const StrategyResult = styled.div<{ $type: InspectionStrategy }>`
  padding: 20px;
  border-radius: 12px;
  background: ${({ theme }) => theme.forecast.background2};
  border-left: 4px solid
    ${({ $type }) =>
      $type === 'A' ? '#4caf50' : $type === 'B' ? '#ff9800' : '#f44336'};
  margin-top: 16px;
  border: 1px solid ${({ theme }) => theme.forecast.border};
  border-left-width: 4px;
`

const StrategyTitle = styled.div<{ $type: InspectionStrategy }>`
  font-size: 18px;
  font-weight: 700;
  margin-bottom: 12px;
  color: ${({ $type }) =>
    $type === 'A' ? '#2e7d32' : $type === 'B' ? '#e65100' : '#c62828'};
  display: flex;
  align-items: center;
  gap: 8px;
`

const StrategyDescription = styled.div`
  font-size: 14px;
  line-height: 1.6;
  color: ${({ theme }) => theme.forecast.defaultText};
  margin-bottom: 16px;
`

const CoverageHighlight = styled.div<{ $type: InspectionStrategy }>`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 12px 16px;
  border-radius: 6px;
  background: ${({ theme }) => theme.forecast.background3};
  border: 1px solid ${({ theme }) => theme.forecast.border};
  font-size: 14px;
  font-weight: 600;
  color: ${({ theme }) => theme.forecast.defaultText};
`

const CoverageValue = styled.span<{ $type: InspectionStrategy }>`
  font-size: 20px;
  font-weight: 700;
  color: ${({ $type }) =>
    $type === 'A' ? '#2e7d32' : $type === 'B' ? '#e65100' : '#c62828'};
`

export interface NiiStrategyPanelProps {
  strategy?: NiiStrategy
  onChange: (strategy: NiiStrategy) => void
  readonly?: boolean
  zoneId: string
}

export const NiiStrategyPanel: FC<NiiStrategyPanelProps> = ({
  strategy,
  onChange,
  readonly,
  zoneId,
}) => {
  const anchor = useZonePropertyAnchor(zoneId)
  const theme = useTheme()

  const degradationLikely = strategy?.degradation_likely
  const degradationPredictable = strategy?.degradation_predictable
  const degradationSeverity = strategy?.degradation_severity

  // Determine strategy based on answers (auto-calculated)
  const calculatedStrategy = useMemo<InspectionStrategy>(() => {
    if (degradationLikely === false) return InspectionStrategy.A
    if (degradationLikely === true) {
      if (degradationPredictable === false) return InspectionStrategy.C
      if (degradationPredictable === true) {
        if (degradationSeverity === 'low' || degradationSeverity === 'medium') {
          return InspectionStrategy.B
        }
        return InspectionStrategy.C
      }
    }
    return InspectionStrategy.B // Default
  }, [degradationLikely, degradationPredictable, degradationSeverity])

  // Effective strategy (override takes precedence)
  const effectiveStrategy =
    strategy?.strategy_type_override ??
    strategy?.strategy_type_calculated ??
    calculatedStrategy

  const baseCoverage = getBaseCoverage(effectiveStrategy)

  const degradationLikelyJustification =
    strategy?.degradation_likely_justification
  const degradationPredictableJustification =
    strategy?.degradation_predictable_justification
  const degradationSeverityJustification =
    strategy?.degradation_severity_justification

  const handleUpdate = (updates: Partial<NiiStrategy>) => {
    // Spread merge — explicit `undefined` in updates correctly clears existing values
    const merged = { ...strategy, ...updates }

    const likelyValue = merged.degradation_likely
    let updatedPredictable = merged.degradation_predictable
    let updatedSeverity = merged.degradation_severity
    const likelyJustification = merged.degradation_likely_justification
    let updatedPredictableJust = merged.degradation_predictable_justification
    let updatedSeverityJust = merged.degradation_severity_justification

    // Clear subsequent questions if Q1 changes to "No"
    if (updates.degradation_likely === false) {
      updatedPredictable = undefined
      updatedSeverity = undefined
      updatedPredictableJust = undefined
      updatedSeverityJust = undefined
    }
    // Clear Q3 if Q2 changes to "No"
    else if (updates.degradation_predictable === false) {
      updatedSeverity = undefined
      updatedSeverityJust = undefined
    }

    // Recalculate strategy with updated values
    let recalculatedStrategy: InspectionStrategy = InspectionStrategy.B
    if (likelyValue === false) {
      recalculatedStrategy = InspectionStrategy.A
    } else if (likelyValue === true) {
      if (updatedPredictable === false) {
        recalculatedStrategy = InspectionStrategy.C
      } else if (updatedPredictable === true) {
        if (updatedSeverity === 'low' || updatedSeverity === 'medium') {
          recalculatedStrategy = InspectionStrategy.B
        } else {
          recalculatedStrategy = InspectionStrategy.C
        }
      }
    }

    // Auto-set top-level justification to the last answered step's justification
    let derivedJustification: string | undefined
    if (likelyValue === false) {
      derivedJustification = likelyJustification
    } else if (likelyValue === true) {
      if (updatedPredictable === false) {
        derivedJustification = updatedPredictableJust
      } else if (updatedPredictable === true && updatedSeverity !== undefined) {
        derivedJustification = updatedSeverityJust
      } else {
        derivedJustification = updatedPredictableJust
      }
    } else {
      derivedJustification = likelyJustification
    }

    onChange({
      strategy_type_calculated: recalculatedStrategy,
      degradation_likely: likelyValue,
      degradation_likely_justification: likelyJustification,
      degradation_predictable: updatedPredictable,
      degradation_predictable_justification: updatedPredictableJust,
      degradation_severity: updatedSeverity,
      degradation_severity_justification: updatedSeverityJust,
      justification: derivedJustification,
      strategy_type_override: merged.strategy_type_override,
    })
  }

  const handleOverrideChange = (value: InspectionStrategy) => {
    // Only save override if it's different from calculated
    const override = value !== calculatedStrategy ? value : undefined
    handleUpdate({ strategy_type_override: override })
  }

  const strategyInfo = {
    A: {
      title: 'Type A - Confirmation Strategy',
      description:
        'Degradation is not expected to be measurable within two inspection intervals. Inspection aims to confirm assumptions.',
      baseCoverage: '7.5%',
    },
    B: {
      title: 'Type B - Quantitative Strategy (Predictable)',
      description:
        'Degradation is predictable and measurable. Inspection quantifies degradation rates and patterns.',
      baseCoverage: '25%',
    },
    C: {
      title: 'Type C - Quantitative Strategy (Unpredictable)',
      description:
        'Degradation is unpredictable or aggressive. More comprehensive inspection required to capture worst-case scenarios.',
      baseCoverage: '100%',
    },
  }

  return (
    <Panel>
      <HelpText>
        Determine the appropriate NII strategy for this zone by answering the
        decision tree questions below. The strategy determines the base
        inspection coverage requirement.
      </HelpText>

      <DecisionTreeCard title='Decision Tree'>
        <CommentIndicator anchor={anchor('nii_strategy.degradation_likely')}>
          <QuestionRow>
            <QuestionIcon $answered={degradationLikely !== undefined}>
              {degradationLikely !== undefined ? (
                <CheckCircle size={20} />
              ) : (
                <HelpCircle size={20} />
              )}
            </QuestionIcon>
            <QuestionContent>
              <QuestionText>
                Q1: Is degradation likely or measurable within two inspection
                intervals?
              </QuestionText>
              <Radio.Group
                value={degradationLikely}
                onChange={(e) =>
                  handleUpdate({ degradation_likely: e.target.value })
                }
                disabled={readonly}>
                <Radio value={true}>Yes - Degradation expected</Radio>
                <Radio value={false}>No - Degradation unlikely</Radio>
              </Radio.Group>
              {degradationLikely !== undefined && (
                <TextArea
                  style={{ marginTop: 8 }}
                  value={degradationLikelyJustification ?? ''}
                  onChange={(e) =>
                    handleUpdate({
                      degradation_likely_justification:
                        e.target.value || undefined,
                    })
                  }
                  placeholder='Justify your answer to Q1 — e.g., based on process conditions, historical inspection data, or known corrosion mechanisms...'
                  rows={2}
                  disabled={readonly}
                />
              )}
            </QuestionContent>
          </QuestionRow>
        </CommentIndicator>

        {degradationLikely === true && (
          <CommentIndicator
            anchor={anchor('nii_strategy.degradation_predictable')}>
            <QuestionRow>
              <QuestionIcon $answered={degradationPredictable !== undefined}>
                {degradationPredictable !== undefined ? (
                  <CheckCircle size={20} />
                ) : (
                  <HelpCircle size={20} />
                )}
              </QuestionIcon>
              <QuestionContent>
                <QuestionText>Q2: Is degradation predictable?</QuestionText>
                <Radio.Group
                  value={degradationPredictable}
                  onChange={(e) =>
                    handleUpdate({ degradation_predictable: e.target.value })
                  }
                  disabled={readonly}>
                  <Radio value={true}>Yes - Predictable pattern</Radio>
                  <Radio value={false}>No - Unpredictable</Radio>
                </Radio.Group>
                {degradationPredictable !== undefined && (
                  <TextArea
                    style={{ marginTop: 8 }}
                    value={degradationPredictableJustification ?? ''}
                    onChange={(e) =>
                      handleUpdate({
                        degradation_predictable_justification:
                          e.target.value || undefined,
                      })
                    }
                    placeholder='Justify your answer to Q2 — e.g., whether a consistent degradation rate exists based on process chemistry, temperature, or prior measurements...'
                    rows={2}
                    disabled={readonly}
                  />
                )}
              </QuestionContent>
            </QuestionRow>
          </CommentIndicator>
        )}

        {degradationLikely === true && degradationPredictable === true && (
          <CommentIndicator
            anchor={anchor('nii_strategy.degradation_severity')}>
            <QuestionRow>
              <QuestionIcon $answered={degradationSeverity !== undefined}>
                {degradationSeverity !== undefined ? (
                  <CheckCircle size={20} />
                ) : (
                  <HelpCircle size={20} />
                )}
              </QuestionIcon>
              <QuestionContent>
                <QuestionText>
                  Q3: What is the degradation severity?
                </QuestionText>
                <Select
                  value={degradationSeverity}
                  onChange={(value) =>
                    handleUpdate({ degradation_severity: value })
                  }
                  placeholder='Select severity'
                  style={{ width: '100%' }}
                  disabled={readonly}>
                  <Select.Option value='low'>Low</Select.Option>
                  <Select.Option value='medium'>Medium</Select.Option>
                  <Select.Option value='high'>High</Select.Option>
                </Select>
                {degradationSeverity !== undefined && (
                  <TextArea
                    style={{ marginTop: 8 }}
                    value={degradationSeverityJustification ?? ''}
                    onChange={(e) =>
                      handleUpdate({
                        degradation_severity_justification:
                          e.target.value || undefined,
                      })
                    }
                    placeholder='Justify the severity rating — e.g., expected corrosion rates, wall thickness loss, known worst-case scenarios...'
                    rows={2}
                    disabled={readonly}
                  />
                )}
              </QuestionContent>
            </QuestionRow>
          </CommentIndicator>
        )}
      </DecisionTreeCard>

      {degradationLikely !== undefined && (
        <StrategyResult $type={effectiveStrategy}>
          <StrategyTitle $type={effectiveStrategy}>
            {effectiveStrategy === InspectionStrategy.A
              ? '✓'
              : effectiveStrategy === InspectionStrategy.B
                ? '◆'
                : '⚠'}{' '}
            {strategyInfo[effectiveStrategy].title}
            {strategy?.strategy_type_override && (
              <Tag color='orange' style={{ marginLeft: 8 }}>
                Overridden
              </Tag>
            )}
          </StrategyTitle>
          <StrategyDescription>
            {strategyInfo[effectiveStrategy].description}
          </StrategyDescription>
          <CoverageHighlight $type={effectiveStrategy}>
            <span>Base Coverage:</span>
            <CoverageValue $type={effectiveStrategy}>
              {baseCoverage}%
            </CoverageValue>
          </CoverageHighlight>
          <div
            style={{
              fontSize: 12,
              color: theme.forecast.tertiaryText,
              marginTop: 8,
              fontStyle: 'italic',
            }}>
            This base coverage will be adjusted by risk factors to determine
            final required coverage.
          </div>
        </StrategyResult>
      )}

      <CommentIndicator anchor={anchor('nii_strategy.strategy_type_override')}>
        <FieldGroup>
          <Label>
            Manual Override (if needed)
            {!readonly && strategy?.strategy_type_override && (
              <Tag
                color='orange'
                style={{ marginLeft: 8, cursor: 'pointer' }}
                icon={<RotateCcw size={12} />}
                onClick={() =>
                  handleUpdate({ strategy_type_override: undefined })
                }
                closable={false}>
                Reset to Calculated
              </Tag>
            )}
          </Label>
          <Select<InspectionStrategy>
            value={effectiveStrategy}
            onChange={handleOverrideChange}
            disabled={readonly}
            style={{ width: '100%' }}>
            <Select.Option value={InspectionStrategy.A}>
              Type A - Confirmation
              {calculatedStrategy === InspectionStrategy.A && ' (Calculated)'}
            </Select.Option>
            <Select.Option value={InspectionStrategy.B}>
              Type B - Quantitative (Predictable)
              {calculatedStrategy === InspectionStrategy.B && ' (Calculated)'}
            </Select.Option>
            <Select.Option value={InspectionStrategy.C}>
              Type C - Quantitative (Unpredictable)
              {calculatedStrategy === InspectionStrategy.C && ' (Calculated)'}
            </Select.Option>
          </Select>
        </FieldGroup>
      </CommentIndicator>
    </Panel>
  )
}
